import React, { useEffect, useState } from 'react'
import { Alert, Button, Tag } from 'antd'
import { DownOutlined, FileTextOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { supplyCardPath } from '../model'

type Side = { supplierTaxId?: string; buyer: string; date: string; net: number | string; vat: number | string; gross: number | string
  items: Array<{ name: string; code?: string; quantity: string | number; unit: string; variant?: string; variantCode?: string; supplierCode?: string; suggestedFor?: number }> }
type Comparison = { number: string; posted: boolean; supplier_by_name?: boolean; buyer_matches: boolean; invoice: Side; receipt: Side }
const money = (n: number | string) => n == null || n === '' ? '—' : Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const items = (side: Side) => side.items.map((row, index) => <div key={index}>{row.name}{row.code && <small> · код {row.code}</small>} — {row.quantity} {row.unit}
  {row.suggestedFor && <div><strong>Для строки {row.suggestedFor}</strong>{row.supplierCode && <> · карточка поставщика {row.supplierCode}</>}</div>}
  {row.variantCode && <small>Характеристика: {row.variant || row.variantCode} · {row.variantCode}</small>}
</div>)

export default function ReceiptComparison({ reviewId, documentId, version }: { reviewId: string; documentId: string; version: number }) {
  const [rows, setRows] = useState<Comparison[]>([]), [error, setError] = useState(false), [retry, setRetry] = useState(0)
  useEffect(() => {
    let active = true
    setRows([]); setError(false)
    fetchAPI(`/api/supply/reviews/${reviewId}/documents/${documentId}/receipt-comparison`)
      .then(result => {
        if (!Array.isArray(result.data) || result.error || result.errors) throw Error('Сверка недоступна')
        if (active) setRows(result.data)
      })
      .catch(() => { if (active) setError(true) })
    return () => { active = false }
  }, [reviewId, documentId, version, retry])
  if (error) return <Alert type="warning" message="Не удалось сверить накладную с приходными в 1С" action={<Button size="small" onClick={() => setRetry(retry + 1)}>Повторить</Button>} />
  return <>{rows.map(row => <ReceiptStrip key={row.number} row={row} />)}</>
}

function ReceiptStrip({ row }: { row: Comparison }) {
  const [expanded, setExpanded] = useState(false)
  const suggested = row.receipt.items.filter(item => item.suggestedFor).length
  const fields = [
    ...(row.supplier_by_name ? [{ label: 'ИНН поставщика', left: row.invoice.supplierTaxId || 'Не указан', right: row.receipt.supplierTaxId || 'Не указан', different: true, group: 'ИНН поставщика' }] : []),
    { label: 'Покупатель', left: row.invoice.buyer, right: row.receipt.buyer, different: !row.buyer_matches, group: 'покупатель' },
    { label: 'Дата документа', left: row.invoice.date, right: row.receipt.date, group: 'дата' },
    ...([['Без НДС', 'net'], ['НДС', 'vat'], ['С НДС', 'gross']] as const).map(([label, key]) => ({
      label, left: money(row.invoice[key]), right: money(row.receipt[key]), group: 'суммы',
    })),
  ].map(field => ({ ...field, differs: 'different' in field ? field.different : field.left !== field.right }))
  const differences = [...new Set(fields.filter(field => field.differs).map(field => field.group))]
  const summary = differences.length === 1 && differences[0] !== 'суммы' ? 'Отличается' : 'Различаются'
  const list = differences.length > 1 ? differences.slice(0, -1).join(', ') + ' и ' + differences[differences.length - 1] : differences[0]
  return <section className="supply-check__receipt-comparison">
    <div className="supply-check__receipt-strip">
      <Link to={supplyCardPath('receipts', row.number)} target="_blank" rel="noopener noreferrer">
        <FileTextOutlined /> Приходная 1С № {row.number}
      </Link>
      <Tag color={row.posted ? 'green' : undefined}>{row.posted ? 'Проведена' : 'Не проведена'}</Tag>
      {row.supplier_by_name && <Tag>Совпадение по названию поставщика</Tag>}
      {differences.length > 0 && <Tag color="gold">{summary} {list}</Tag>}
      <Button type="text" size="small" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Свернуть' : suggested ? `Сравнить · подбор строк: ${suggested}` : 'Сравнить'} <DownOutlined rotate={expanded ? 180 : 0} />
      </Button>
    </div>
    {expanded && <div className="supply-check__receipt-fields"><table><thead><tr><th>Поле</th><th>В накладной</th><th>В 1С</th></tr></thead><tbody>
      {fields.map(field => <tr key={field.label}><th>{field.label}</th><td>{field.left || '—'}</td><td className={field.differs ? 'is-different' : ''}>{field.right || '—'}</td></tr>)}
      <tr><th>Товары</th><td>{items(row.invoice)}</td><td>{items(row.receipt)}</td></tr>
    </tbody></table></div>}
  </section>
}
